import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {useGetProductContext} from "../context/get-product";
import {OfferingFormCheckoutFormValues} from "../../../../components/offering-form/general/checkout-form/types";
import {offeringCheckoutFormValidationSchema} from "../../../../components/offering-form/general/checkout-form/validation-schema";
import {useEditProductHook} from "../hooks/edit-product";
import {useNavigate} from "react-router-dom";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {EditProductFormValues} from "../types";
import {FormWrapper} from "../../../../components/form-wrapper";
import {ProductCheckoutFormFields} from "../../../../components/offering-form/products/checkout-form";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useDeleteProductHook} from "../hooks/delete-product";
import {toSavedEditProductFormValues} from "../saved-form-values";
import {IS_PWYW_ENABLED} from "../../../../services/config";

export function EditProductCheckoutForm() {
  const navigate = useNavigate();
  const {
    state: {productData},
  } = useGetProductContext();
  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: productData.product.bookingQuestions
        ? productData.product.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
              };
            }
          })
        : [],
    },
  });
  const {editProduct, isEditingProduct} = useEditProductHook();
  const {deleteProduct} = useDeleteProductHook(productData.product.id);
  function onSubmit(values: OfferingFormCheckoutFormValues) {
    // The update replaces the whole product, so everything this tab does not edit goes
    // back exactly as saved: redirect, purchase limit, pricing model and currencies.
    const payload: EditProductFormValues = {
      ...toSavedEditProductFormValues(productData, IS_PWYW_ENABLED),
      id: productData.product.id,
      bookingQuestions: values.bookingQuestions,
    };
    editProduct(payload);
  }

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  return (
    <FormWrapper>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <ProductCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => navigate("/products")}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                    loading={isEditingProduct}
                  >
                    Update product
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this product? This action cannot be undone."
                    okText="Delete product"
                    onOk={deleteProduct}
                    title="Confirm product deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </FormWrapper>
  );
}
